import { run as suggestEmail, POPULAR_DOMAINS } from '@zootools/email-spell-checker';

/**
 * "Did you mean …@gmail.com?" under the email fields of the lead forms
 * (#web2lead, partial/_webtolead.antlers.html, and #bf23lead,
 * landings/bf.antlers.html).
 *
 * Opt-in per field: any `input[type="email"]` carrying `data-email-suggest`
 * gets it. The attribute holds the localised sentence with an `:email`
 * placeholder (`custom.email_suggest`), replaced here by a button with the
 * suggested address. The value is never changed unless the visitor clicks the
 * suggestion: an unusual domain may well be a real one (a company mailbox),
 * and it must still be possible to submit it as typed.
 */

// The library's default list is US-centric (no hotmail, outlook, yahoo,
// libero…): these are the mailboxes of the six markets of the site.
const DOMAINS = [...new Set([
    ...POPULAR_DOMAINS,
    // International
    'gmail.com', 'googlemail.com', 'icloud.com', 'me.com', 'mac.com',
    'hotmail.com', 'outlook.com', 'live.com', 'msn.com',
    'yahoo.com', 'aol.com', 'proton.me', 'protonmail.com', 'gmx.com', 'mail.com', 'email.com',
    // it
    'libero.it', 'virgilio.it', 'alice.it', 'tiscali.it', 'tim.it', 'fastwebnet.it',
    'hotmail.it', 'outlook.it', 'live.it', 'yahoo.it', 'email.it', 'inwind.it',
    'tin.it', 'pec.it', 'legalmail.it',
    // de / at / ch
    'gmx.de', 'gmx.net', 'gmx.at', 'gmx.ch', 'web.de', 't-online.de', 'freenet.de',
    'posteo.de', 'arcor.de', 'hotmail.de', 'outlook.de', 'yahoo.de', 'bluewin.ch',
    // fr / be
    'orange.fr', 'free.fr', 'laposte.net', 'sfr.fr', 'wanadoo.fr', 'neuf.fr',
    'hotmail.fr', 'outlook.fr', 'live.fr', 'yahoo.fr', 'skynet.be', 'telenet.be',
    // nl
    'ziggo.nl', 'kpnmail.nl', 'planet.nl', 'home.nl', 'xs4all.nl', 'hotmail.nl',
    'outlook.nl', 'live.nl', 'casema.nl',
    // pl
    'wp.pl', 'o2.pl', 'onet.pl', 'op.pl', 'interia.pl', 'interia.eu', 'gazeta.pl',
    'poczta.fm', 'tlen.pl',
])];

// Mailboxes that only exist on .com: "gmail.it" or "gmai.it" is a Gmail
// address, not email.it or gmx.it, which is what the fuzzy match finds.
const COM_ONLY = ['gmail.com', 'googlemail.com', 'icloud.com'];

// Only the providers that really sit on several TLDs (hotmail.it, yahoo.fr…).
// The library's default also has "mail", which turned "gmai.it" into "mail.it".
const SECOND_LEVEL_DOMAINS = ['yahoo', 'hotmail', 'live', 'outlook'];

/**
 * The corrected address for `email`, or undefined when it looks right (or is
 * too far from any known mailbox to guess).
 */
export function suggestFor(email) {
    const at = email.lastIndexOf('@');
    if (at < 1) return undefined;

    const address = email.slice(0, at);
    const domain = email.slice(at + 1).toLowerCase();
    if (DOMAINS.includes(domain)) return undefined;

    const label = domain.split('.')[0];
    if (label === '') return undefined;

    if (label.length > 3 && !domain.endsWith('.com')) {
        const exact = COM_ONLY.find(d => d === label + '.com');
        const close = exact || suggestEmail({
            email: address + '@' + label + '.com',
            domains: COM_ONLY,
            topLevelDomains: ['com'],
            secondLevelDomains: COM_ONLY.map(d => d.split('.')[0]),
        })?.domain;
        if (close) return address + '@' + close;
    }

    const suggestion = suggestEmail({ email, domains: DOMAINS, secondLevelDomains: SECOND_LEVEL_DOMAINS });
    if (!suggestion) return undefined;

    // Two or three letters are within the fuzzy threshold of almost anything
    // (hp.com → qq.com, ibm.com → aim.com): for those, fix the TLD only.
    if (label.length <= 3 && suggestion.domain.split('.')[0] !== label) return undefined;

    return suggestion.full;
}

// How long the visitor has to stop typing before the field is checked.
const TYPING_PAUSE_MS = 800;

// The length of the open/close transition in index.scss (.email-suggest).
const TRANSITION_MS = 150;

// A domain with a dot and a two-letter TLD: "mario@gm" or "mario@gmail.c"
// is not a typo, it is still being typed.
const LOOKS_COMPLETE = /@[^@\s]+\.[a-z]{2,}$/i;

export function initEmailSuggest() {
    document.querySelectorAll('input[type="email"][data-email-suggest]').forEach(attach);
}

function attach(input) {
    // "Did you mean :email?" — built once; only the button's text changes
    // afterwards, so the button is never swapped out from under the pointer
    // or the keyboard focus.
    const [before, after = ''] = input.dataset.emailSuggest.split(':email');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'email-suggest__apply';

    const body = document.createElement('div');
    body.className = 'email-suggest__body';
    body.append(before, button, after);

    const hint = document.createElement('div');
    hint.className = 'email-suggest';
    hint.id = (input.id || input.name) + '-suggest';
    hint.setAttribute('aria-live', 'polite');
    hint.append(body);
    input.insertAdjacentElement('afterend', hint);
    // The hint takes over the field's bottom margin. A closed hint is still in
    // the flow (it has to be, to slide open), and an element between the field
    // and the end of its column stops that margin from collapsing with the
    // column's own: on #bf23lead that alone added 16px under every email field.
    hint.style.setProperty('--email-suggest-gap', getComputedStyle(input).marginBottom);
    input.style.marginBottom = '0';
    input.setAttribute('aria-describedby', [input.getAttribute('aria-describedby'), hint.id].filter(Boolean).join(' '));

    let typingTimer = null;
    let clearTimer = null;

    const show = suggestion => {
        clearTimeout(clearTimer);
        if (button.textContent !== suggestion) button.textContent = suggestion;
        hint.classList.add('is-open');
    };

    const hide = () => {
        clearTimeout(typingTimer);
        if (!hint.classList.contains('is-open')) return;
        hint.classList.remove('is-open');
        // Emptied once the collapse is over, so the next suggestion is new
        // content for the live region even if it is the same address.
        clearTimer = setTimeout(() => { button.textContent = ''; }, TRANSITION_MS);
    };

    const check = () => {
        const suggestion = suggestFor(input.value.trim());
        if (suggestion) show(suggestion);
        else hide();
    };

    // Checked when the visitor leaves the field, and once they pause typing
    // in it. Typing does not hide the hint: it stays as it is until the pause,
    // then changes, or goes if the address now looks right, so the fields
    // below move once instead of at every keystroke.
    input.addEventListener('blur', () => {
        clearTimeout(typingTimer);
        check();
    });
    input.addEventListener('input', () => {
        clearTimeout(typingTimer);
        typingTimer = setTimeout(() => {
            if (LOOKS_COMPLETE.test(input.value.trim())) check();
            else hide();
        }, TYPING_PAUSE_MS);
    });

    // Keep the focus in the field: a blur here would re-check before the click.
    button.addEventListener('mousedown', event => event.preventDefault());
    button.addEventListener('click', () => {
        // The text on the button may be from before the last keystrokes:
        // apply what the field holds now, or nothing if it no longer needs it.
        const suggestion = suggestFor(input.value.trim());
        hide();
        if (suggestion) {
            input.value = suggestion;
            // Let the form's own listeners (validation, Turnstile state…) see the change.
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        input.focus();
    });

    if (input.form) {
        input.form.addEventListener('submit', hide);
        input.form.addEventListener('reset', hide);
    }
}
